import React, { useState, useEffect, useCallback } from 'react';
import { ProjectManager } from './components/ProjectManager';
import { EditorLayout } from './components/EditorLayout';
import { useNodeStore } from './stores/nodeStore';
import { useCanvasStore } from './stores/canvasStore';
import { useModelStore } from './stores/modelStore';
import { useTaskStore } from './stores/taskStore';
import { useProjectStore } from './stores/projectStore';
import { useDataPersistence } from './hooks/useDataPersistence';
import { useWorkflowManager } from './hooks/useWorkflowManager';
import { useViewTransition } from './hooks/useViewTransition';
import { Toaster } from 'sonner';

const SPRING = "cubic-bezier(0.32, 0.72, 0, 1)";

export const AppNew = () => {
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isMultiFrameOpen, setIsMultiFrameOpen] = useState(false);
    const [isSonicStudioOpen, setIsSonicStudioOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [sphereFlowing, setSphereFlowing] = useState(true);

    const [isLoaded, setIsLoaded] = useState(false);
    const [dataLoadSucceeded, setDataLoadSucceeded] = useState(false);
    const [isInEditor, setIsInEditor] = useState(false);

    const nodes = useNodeStore((s: any) => s.nodes);
    const connections = useNodeStore((s: any) => s.connections);
    const setNodes = useNodeStore((s: any) => s.setNodes);
    const setConnections = useNodeStore((s: any) => s.setConnections);
    const loadModels = useModelStore((s: any) => s.loadModels);
    const loadFromLocalStorage = useTaskStore((s: any) => s.loadFromLocalStorage);
    const connectWebSocket = useTaskStore((s: any) => s.connectWebSocket);
    const disconnectWebSocket = useTaskStore((s: any) => s.disconnectWebSocket);
    const projects = useProjectStore((s: any) => s.projects);
    const currentProjectId = useProjectStore((s: any) => s.currentProjectId);
    const loadProjects = useProjectStore((s: any) => s.loadProjects);

    const [assetHistory, setAssetHistory] = useState<any>([]);

    const { isTransitioning, transitionDirection } = useViewTransition();

    const {
        workflows, setWorkflows, selectedWorkflowId,
        saveCurrentAsWorkflow, onLoadWorkflow, onCreateProject, onBackToProjects,
        deleteWorkflow, renameWorkflow,
    } = useWorkflowManager({ saveHistory: () => {} });

    const { loadDataFromStorage } = useDataPersistence({
        isLoaded, dataLoadSucceeded, isInEditor, currentProjectId,
        nodes, connections, assetHistory, workflows,
        setNodes, setConnections, setAssetHistory, setWorkflows,
        setDataLoadSucceeded, setIsLoaded, getThumbnail: () => '',
    });

    useEffect(() => {
        if (window.bongai) void window.bongai.hasSelectedApiKey().then((hasKey: boolean) => { if (!hasKey) window.bongai?.openSelectKey(); });
        loadModels().catch((e: any) => console.error('[Init] loadModels failed:', e));
        loadFromLocalStorage();
        loadProjects().catch((e: any) => console.error('[Init] loadProjects failed:', e));
        loadDataFromStorage();
        connectWebSocket();
        return () => { disconnectWebSocket(); };
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setSphereFlowing(prev => !prev);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="w-screen h-screen overflow-hidden bg-[#0a0a0c]">
            <Toaster position="top-center" richColors theme="dark" closeButton />
            {!isInEditor ? (
                <div className={`w-full h-full transition-all duration-500 ease-[${SPRING}] ${isTransitioning && transitionDirection === 'toEditor' ? 'opacity-0 scale-105' : 'opacity-100 scale-100'}`}>
                    <ProjectManager
                        workflows={workflows}
                        projects={projects}
                        onSelectWorkflow={async (id) => { if (id) { await onLoadWorkflow(id); setIsInEditor(true); setDataLoadSucceeded(true); } }}
                        onCreateProject={(title) => onCreateProject(title, setIsInEditor, setDataLoadSucceeded)}
                        onDeleteWorkflow={(id) => { if (id) deleteWorkflow(id); }}
                        onRenameWorkflow={(id, title) => renameWorkflow(id, title, workflows, setWorkflows)}
                    />
                </div>
            ) : (
                <EditorLayout
                    isTransitioning={isTransitioning}
                    transitionDirection={transitionDirection}
                    onBackToProjects={() => onBackToProjects(setIsInEditor)}
                    workflows={workflows}
                    selectedWorkflowId={selectedWorkflowId}
                    onSelectWorkflow={(id) => { onLoadWorkflow(id); }}
                    onSaveWorkflow={saveCurrentAsWorkflow}
                    onDeleteWorkflow={(id) => deleteWorkflow(id)}
                    onRenameWorkflow={(id, title) => renameWorkflow(id, title, workflows, setWorkflows)}
                    onOpenSettings={() => setIsSettingsOpen(true)}
                    isSettingsOpen={isSettingsOpen}
                    onCloseSettings={() => setIsSettingsOpen(false)}
                    isChatOpen={isChatOpen}
                    onToggleChat={() => setIsChatOpen(!isChatOpen)}
                    isMultiFrameOpen={isMultiFrameOpen}
                    onToggleMultiFrame={() => setIsMultiFrameOpen(!isMultiFrameOpen)}
                    isSonicStudioOpen={isSonicStudioOpen}
                    onToggleSonicStudio={() => setIsSonicStudioOpen(!isSonicStudioOpen)}
                    assetHistory={assetHistory}
                    setAssetHistory={setAssetHistory}
                    sphereFlowing={sphereFlowing}
                />
            )}
        </div>
    );
};
